import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrders, updateOrderStatus, exportOrdersExcel } from '../api';
import SortableHeader from '../components/SortableHeader';
import ExportMenu from '../components/ExportMenu';
import { ColumnsButton } from '../components/ColumnConfigurator';
import MultiSelectFilter from '../components/MultiSelectFilter';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import OrdersKanban from '../components/OrdersKanban';
import { Search, ShoppingCart, Edit, Eye, X, LayoutGrid, List, FileText } from 'lucide-react';
import { fmtCurrency } from '../utils/formatCurrency';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';

function Orders() {
  const { canDo } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { visibleColumns } = useColumns('orders');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [viewMode, setViewMode] = useState('table');
  const [statusFilter, setStatusFilter] = useState(() => {
    const param = searchParams.get('status');
    return param ? param.split(',') : [];
  });

  // Build extra params for the pagination hook
  const extraParams = useMemo(() => {
    const p = {};
    if (statusFilter.length) p.status = statusFilter.join(',');
    return p;
  }, [statusFilter]);

  // Server-side pagination via hook (for table view)
  const {
    data: tableData,
    loading,
    error: paginationError,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sortBy: sortField,
    sortOrder: sortDirection,
    handleSort,
    total,
    totalPages,
    refetch,
  } = usePagination(getOrders, {
    queryKey: 'orders',
    initialLimit: 25,
    initialSortBy: 'order_date',
    initialSortOrder: 'desc',
    debounceMs: 300,
    extraParams,
  });

  // For kanban view (separate unpaginated fetch)
  const [kanbanData, setKanbanData] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (viewMode === 'kanban') {
      setKanbanLoading(true);
      getOrders({ search, status: statusFilter.join(',') || undefined })
        .then(res => setKanbanData(Array.isArray(res.data) ? res.data : res.data.data || []))
        .catch(() => setError('Failed to load orders'))
        .finally(() => setKanbanLoading(false));
    }
  }, [viewMode, search, statusFilter]);

  const displayError = error || paginationError;

  const handleKanbanStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setKanbanData(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      setError('Failed to update order status');
      // Re-fetch kanban data to revert
      getOrders({ search, status: statusFilter.join(',') || undefined })
        .then(res => setKanbanData(Array.isArray(res.data) ? res.data : res.data.data || []))
        .catch(() => {});
    }
  };

  const getStatusColor = (status) => {
    switch (status) { case 'Order Created': return 'warning'; case 'Shipped': return 'success'; default: return 'gray'; }
  };

  const getRowActions = (order) => [
    {
      key: 'view',
      label: 'View',
      icon: Eye,
      onClick: () => navigate(`/orders/${order.id}`),
    },
    canDo('orders', 'edit') && {
      key: 'edit',
      label: 'Edit',
      icon: Edit,
      onClick: () => navigate(`/orders/${order.id}`),
    },
  ];

  const renderCell = (col, order) => {
    switch (col.key) {
      case 'order_number':
        return <Link to={`/orders/${order.id}`}>{order.order_number}</Link>;
      case 'account_name':
        return order.account_name ? <Link to={`/accounts/${order.account_id}`}>{order.account_name}</Link> : <EmptyValue />;
      case 'total':
        return order.total != null ? `$${fmtCurrency(order.total)}` : <EmptyValue />;
      case 'status':
        return <span className={`badge badge-${getStatusColor(order.status)}`}>{order.status}</span>;
      case 'order_date':
        return order.order_date ? new Date(order.order_date).toLocaleDateString() : <EmptyValue />;
      case 'created_at':
        return order.created_at ? new Date(order.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return order[col.key] || <EmptyValue />;
    }
  };

  return (
    <div className={`orders-page${viewMode === 'kanban' ? ' kanban-active' : ''}`}>
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
        <div className="page-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <List size={16} />
              Table
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={16} />
              Kanban
            </button>
          </div>
          <ColumnsButton entityType="orders" />
          <ExportMenu
            onExportExcel={exportOrdersExcel}
            entityName="Orders"
          />
        </div>
      </div>

      {displayError && <div className="alert alert-error" role="alert" aria-live="assertive">{displayError}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input type="text" className="form-input" placeholder="Search orders..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search orders" />
            </div>
            <MultiSelectFilter
              label="Status"
              options={[{ id: 'Order Created', name: 'Order Created' }, { id: 'Shipped', name: 'Shipped' }]}
              selected={statusFilter}
              onChange={setStatusFilter}
              className="filter-dropdown-sm"
              ariaLabel="Filter by status"
            />
            {(search || statusFilter.length > 0) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setStatusFilter([]); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        kanbanLoading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : (
          <OrdersKanban
            orders={kanbanData}
            onStatusChange={handleKanbanStatusChange}
            canEdit={canDo('orders', 'edit')}
          />
        )
      ) : isMobile ? (
        /* Mobile Card Layout */
        <div className="card">
          {loading ? (
            <CardSkeleton count={4} />
          ) : tableData.length === 0 ? (
            <div className="card-body">
              {search || statusFilter.length > 0 ? (
                <EmptyStateFiltered
                  onClearFilters={() => { setSearch(''); setStatusFilter([]); }}
                />
              ) : (
                <EmptyState
                  icon={ShoppingCart}
                  title="No orders yet"
                  description="Orders are created by converting accepted quotes. Go to Quotes to create your first order."
                  actionLabel="Go to Quotes"
                  onAction={() => navigate('/quotes')}
                  actionIcon={FileText}
                />
              )}
            </div>
          ) : (
            <>
              {tableData.map(order => (
                <MobileCard
                  key={order.id}
                  columns={visibleColumns}
                  data={order}
                  renderCell={renderCell}
                  primaryField="order_number"
                  actions={<RowActions actions={getRowActions(order)} id={order.id} />}
                />
              ))}
            </>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      ) : (
        /* Desktop Table Layout */
        <div className="card">
          <div className="table-container">
            <table className="table" aria-label="Orders list">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={false} hasActions={true} />
                ) : tableData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      {search || statusFilter.length > 0 ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setStatusFilter([]); }}
                        />
                      ) : (
                        <EmptyState
                          icon={ShoppingCart}
                          title="No orders yet"
                          description="Orders are created by converting accepted quotes. Go to Quotes to create your first order."
                          actionLabel="Go to Quotes"
                          onAction={() => navigate('/quotes')}
                          actionIcon={FileText}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  tableData.map(order => (
                    <tr key={order.id}>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderCell(col, order)}</td>
                      ))}
                      <td>
                        <RowActions actions={getRowActions(order)} id={order.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}
    </div>
  );
}

export default Orders;
